import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaWindows, FaLinux } from 'react-icons/fa';

const techStack = {
  Language: [
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-plain.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg', learning: true },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kotlin/kotlin-original.svg', learning: true },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/solidity/solidity-original.svg', learning: true },
  ],
  Frontend: [
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg', learning: true  },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg', learning: true },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg' },
  ],
  Backend: [
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/laravel/laravel-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg', learning: true  },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nestjs/nestjs-original.svg', learning: true },
  ],
  Database: [
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-original.svg', learning: true },
  ],
  Tools: [
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/androidstudio/androidstudio-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postman/postman-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/jira/jira-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/selenium/selenium-original.svg', learning: true },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/hardhat/hardhat-original.svg', learning: true },
  ],
  Deployment: [
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vercel/vercel-original.svg' },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/netlify/netlify-original.svg' },
    { name: '', img: 'https://cdn.simpleicons.org/render/000000', learning: true },
    { name: '', img: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg', learning: true },
  ],
};

const boxVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, ease: 'easeOut' },
  },
};

const About = () => {
  const aboutRef = useRef(null);
  const techRef = useRef(null);
  const isAboutInView = useInView(aboutRef, { once: true, amount: 0.4 });
  const isTechInView = useInView(techRef, { once: true, amount: 0.4 });

  return (
    <section
      id="about"
      className="min-h-screen py-20 px-6 md:px-20 bg-[#F5F3EF] text-[#3b2f2f] flex justify-center items-stretch"
    >
      <div className="w-full max-w-[1440px] flex flex-col xl:flex-row gap-10">
        {/* About Me */}
        <motion.div
          ref={aboutRef}
          className="w-full xl:w-1/2 h-[680px] bg-white/60 p-8 rounded-xl border border-[#dbcfc1] shadow-md flex flex-col"
          variants={boxVariants}
          initial="hidden"
          animate={isAboutInView ? 'visible' : 'hidden'}
        >
          <div className="overflow-y-auto scrollbar-hide pr-2">
            <h2 className="text-2xl font-bold mb-6 text-[#5e4b45] text-center xl:text-left">About Me</h2>
            <p className="text-md leading-relaxed text-justify mb-5">
              Hey I’m Hugo, a Frontend Developer who build most web apps with React and Tailwind. Frontend is my main focus, but I’m also diving into Vue, Nest.js, and other tools to grow as a fullstack dev. I enjoy learning new things and creating interfaces that feel good to use.  
            </p>
            
            <p className="text-md leading-relaxed text-justify mb-5">
              I'm currently learning mobile development with Kotlin, and also exploring 3D websites and blockchain to stay future-ready. Besides web and mobile development, I’m also interested in learning UI/UX and quality assurance to better understand both user experience and software reliability.
            </p>

            <div className="w-full h-px bg-gray-300 my-6" />

            <div className="flex items-center gap-4 mt-6 text-[#5e4b45]">
              <FaWindows size={24} title="Windows" />
              <FaLinux size={24} title="Linux" />
            </div>
          </div>
        </motion.div>

        {/* Tech Stack */}
        <motion.div
          ref={techRef}
          className="w-full xl:w-1/2 h-[680px] bg-white/60 p-8 rounded-xl border border-[#dbcfc1] shadow-md flex flex-col"
          variants={boxVariants}
          initial="hidden"
          animate={isTechInView ? 'visible' : 'hidden'}
        >
          <h3 className="text-2xl font-semibold mb-6 text-[#5e4b45]">Tech Stack</h3>
          <div className="flex-1 overflow-y-auto scrollbar-hide pr-2 space-y-10">
            {Object.entries(techStack).map(([category, items]) => (
              <div key={category}>
                <h4 className="text-lg font-semibold mb-4 text-[#3b2f2f]">{category}:</h4>
                <div className="flex flex-wrap gap-4">
                {items.map((item, idx) => (
                    <div
                      key={`${category}-${idx}`}
                      className="flex flex-col items-center text-center relative"
                      title={item.name}
                    >
                      <img
                        src={item.img}
                        alt={item.name}
                        className="w-9 h-9 object-contain"
                      />
                      {item.learning && (
                        <span className="absolute -top-1 -right-2 text-[9px] bg-yellow-500 text-white px-1 rounded-md">learning</span>
                      )}
                      <span className="text-xs mt-1">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
